import { z } from 'zod';
import { TOS_VERSION } from './tos.js';

/**
 * SIL-1050 — individual/business self-signup from a referral link.
 * docs/superpowers/specs/2026-09-13-referral-code-signup-design.md
 * ("Public endpoints" / "DTOs" sections).
 *
 * Password rule mirrors the Cognito pool's password policy (8+, upper,
 * lower, number, symbol — infra/modules/cognito-client/main.tf's
 * `password_policy` block). tosVersion must equal the CURRENT TOS_VERSION —
 * a stale version is rejected client-side here AND re-checked server-side
 * (provision_referral_signup, db/schema/883) against current_tos_version().
 */
export const SIGNUP_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

export const signupPasswordSchema = z
  .string()
  .min(8, 'At least 8 characters')
  .regex(SIGNUP_PASSWORD_REGEX, 'Must include an uppercase letter, a lowercase letter, a number, and a symbol');

/**
 * SIL-1050 follow-up (visible password validation): per-rule checks derived
 * from the SAME regex/length rule above, one source of truth. The signup
 * form renders these as a live checklist under the password field instead of
 * leaving the visitor to guess why the Sign up button is disabled — each
 * rule's `test` is a single clause of SIGNUP_PASSWORD_REGEX, kept in sync by
 * hand since a compiled RegExp can't be decomposed into its lookaheads.
 */
export interface PasswordRule {
  key: 'length' | 'uppercase' | 'lowercase' | 'number' | 'symbol';
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  { key: 'length', label: 'At least 8 characters', test: (password) => password.length >= 8 },
  { key: 'uppercase', label: 'An uppercase letter', test: (password) => /[A-Z]/.test(password) },
  { key: 'lowercase', label: 'A lowercase letter', test: (password) => /[a-z]/.test(password) },
  { key: 'number', label: 'A number', test: (password) => /\d/.test(password) },
  { key: 'symbol', label: 'A symbol', test: (password) => /[^A-Za-z0-9]/.test(password) },
];

/** Profile fields collected at signup time — minimum only (spec's "Form
 * fields" decision table). Remaining profile fields are collected by the
 * existing post-login gates (IndividualProfileGate / onboarding wizard). */
export const individualSignupProfileSchema = z.object({
  organizationType: z.literal('individual'),
  firstName: z.string().trim().min(1).max(200),
  lastName: z.string().trim().min(1).max(200),
  countryOfResidence: z.string().trim().length(2), // ISO 3166-1 alpha-2
});
export type IndividualSignupProfile = z.infer<typeof individualSignupProfileSchema>;

export const businessSignupProfileSchema = z.object({
  organizationType: z.literal('business'),
  legalName: z.string().trim().min(1).max(300),
  contactName: z.string().trim().min(1).max(200),
  countryOfResidence: z.string().trim().length(2),
});
export type BusinessSignupProfile = z.infer<typeof businessSignupProfileSchema>;

export const signupProfileSchema = z.discriminatedUnion('organizationType', [
  individualSignupProfileSchema,
  businessSignupProfileSchema,
]);
export type SignupProfile = z.infer<typeof signupProfileSchema>;

/** POST /api/pub/signup/lookup */
export const signupLookupRequestSchema = z.object({
  code: z.string().trim().min(1).max(64),
});
export type SignupLookupRequest = z.infer<typeof signupLookupRequestSchema>;

export interface SignupLookupResponse {
  organizationType: 'individual' | 'business';
}

/** POST /api/pub/signup
 *
 * SIL-1059: `tosAccepted` must be the literal `true` — a clickwrap checkbox,
 * unchecked by default (apps/customer/src/routes/signup.tsx). Zod rejects
 * `false`/missing/any other value with a 400 before this route reserves a
 * code, calls Cognito, or writes anything (docs/superpowers/specs/
 * 2026-09-10-tos-clickwrap-gate-design.md — acceptance must be a forced,
 * logged, affirmative action).
 */
export const signupRequestSchema = z.object({
  code: z.string().trim().min(1).max(64),
  email: z.string().trim().email().max(320),
  password: signupPasswordSchema,
  profile: signupProfileSchema,
  tosVersion: z.literal(TOS_VERSION),
  tosAccepted: z.literal(true, {
    errorMap: () => ({ message: 'You must agree to the Terms of Service' }),
  }),
  turnstileToken: z.string().min(1),
});
export type SignupRequest = z.infer<typeof signupRequestSchema>;

export interface SignupResponse {
  status: 'verification_sent';
  /** Kept in the browser's memory only — never persisted client-side, never
   * logged server-side. Sent back with /confirm and /resend. */
  reservationSecret: string;
}

/** POST /api/pub/signup/confirm */
export const signupConfirmRequestSchema = z.object({
  code: z.string().trim().min(1).max(64),
  email: z.string().trim().email().max(320),
  password: signupPasswordSchema,
  verificationCode: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code'),
  reservationSecret: z.string().min(1),
});
export type SignupConfirmRequest = z.infer<typeof signupConfirmRequestSchema>;

export interface SignupConfirmResponse {
  status: 'created';
}

/** POST /api/pub/signup/resend */
export const signupResendRequestSchema = z.object({
  code: z.string().trim().min(1).max(64),
  email: z.string().trim().email().max(320),
  password: signupPasswordSchema,
  reservationSecret: z.string().min(1),
  turnstileToken: z.string().min(1),
});
export type SignupResendRequest = z.infer<typeof signupResendRequestSchema>;

export interface SignupResendResponse {
  status: 'verification_sent';
}

/**
 * Uniform failure shapes (spec's "Responses are uniform" — never reveal
 * whether a code or email exists). lookup: 404 invalid_code. signup/resend:
 * always 202 verification_sent (even on failure — see routes/pub/signup.ts).
 * confirm: 400 invalid_or_expired.
 */
export interface SignupInvalidCodeError {
  error: 'invalid_code';
}
export interface SignupInvalidOrExpiredError {
  error: 'invalid_or_expired';
}
